import { router } from '@inertiajs/react'
import { useState } from 'react'

import { csrfToken } from '../../../utils/csrfToken'

function FlashAlert({ variant, message, onDismiss, textDark }) {
  if (!message) return null

  const className = [
    'alert',
    `alert-${variant}`,
    textDark ? 'text-dark' : '',
    'alert-dismissible',
    'fade',
    'show',
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <div className={className} role="alert">
      <h4 className="alert-heading mb-0">{message}</h4>
      <button
        type="button"
        className="btn-close"
        data-bs-dismiss="alert"
        aria-label="Close"
        onClick={onDismiss}
      ></button>
    </div>
  )
}

export default function Show({ hubstaff, errors }) {
  const [apiKey, setApiKey] = useState('')
  const [saving, setSaving] = useState(false)
  const [clearing, setClearing] = useState(false)
  const [fieldErrors, setFieldErrors] = useState(errors && Object.keys(errors).length ? errors : null)
  const [flashAlert, setFlashAlert] = useState(null)

  const configured = hubstaff?.configured
  const maskedValue = configured ? `••••••••${hubstaff.lastFive || ''}` : 'Not configured'

  async function submit(e) {
    e.preventDefault()
    setSaving(true)
    setFlashAlert(null)
    setFieldErrors(null)

    const response = await fetch('/system_settings/third_party_integrations', {
      method: 'PATCH',
      headers: {
        'X-CSRF-Token': csrfToken() || '',
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ hubstaff_api_key: apiKey }),
      credentials: 'same-origin',
    })

    if (response.status === 200 || response.status === 204 || response.redirected) {
      router.visit('/system_settings/third_party_integrations')
      return
    }

    if (response.status === 422) {
      const data = await response.json()
      setFieldErrors(data.errors && Object.keys(data.errors).length ? data.errors : null)
      if (data.message) {
        setFlashAlert(data.message)
      }
      setSaving(false)
      return
    }

    setFlashAlert(`Failed to save Hubstaff API key: ${response.status}`)
    setSaving(false)
  }

  async function clearKey() {
    if (!confirm('Are you sure you want to clear the Hubstaff API key?')) return

    setClearing(true)
    setFlashAlert(null)

    const response = await fetch('/system_settings/third_party_integrations', {
      method: 'DELETE',
      headers: {
        'X-CSRF-Token': csrfToken() || '',
        Accept: 'application/json',
      },
      credentials: 'same-origin',
    })

    if (response.status === 200 || response.status === 204 || response.redirected) {
      router.visit('/system_settings/third_party_integrations')
      return
    }

    setFlashAlert(`Failed to clear Hubstaff API key: ${response.status}`)
    setClearing(false)
  }

  return (
    <div className="container-fluid">
      <div className="header">
        <div className="header-body">
          <div className="row align-items-end">
            <div className="col">
              <h6 className="header-pretitle">System Settings</h6>
              <h1 className="header-title">Third Party Integrations</h1>
            </div>
          </div>
        </div>
      </div>

      <FlashAlert variant="danger" message={flashAlert} onDismiss={() => setFlashAlert(null)} />

      <div className="row">
        <div className="col-12 col-lg-6">
          <div className="card">
            <div className="card-header">
              <h4 className="card-header-title">Hubstaff</h4>
            </div>
            <div className="card-body">
              <div className="form-group">
                <label className="form-label">Current API Key</label>
                <p className="mb-0">{maskedValue}</p>
              </div>

              <form onSubmit={submit}>
                <div className="form-group">
                  <label className="form-label">New API Key</label>
                  <input
                    type="password"
                    className="form-control"
                    value={apiKey}
                    onChange={(e) => setApiKey(e.target.value)}
                    placeholder="Enter a new Hubstaff API key"
                    autoComplete="off"
                  />
                  {fieldErrors?.hubstaff_api_key ? (
                    <div className="invalid-feedback d-block">{fieldErrors.hubstaff_api_key.join(', ')}</div>
                  ) : null}
                </div>

                <div className="d-flex gap-2">
                  <button type="submit" className="btn btn-primary" disabled={saving}>
                    {saving ? 'Saving…' : 'Save'}
                  </button>
                  {configured ? (
                    <button
                      type="button"
                      className="btn btn-danger"
                      onClick={clearKey}
                      disabled={clearing}
                    >
                      {clearing ? 'Clearing…' : 'Clear'}
                    </button>
                  ) : null}
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
